/* eslint-disable react-refresh/only-export-components --
 * Route configuration module: its public export is the `router` object, and the
 * small components here exist only to wrap lazy routes.
 */
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import Layout from '@/shared/components/layout/Layout'
import ProtectedRoute from '@/features/auth/components/ProtectedRoute'
import AuthGate from '@/features/auth/components/AuthGate'
import RequirePermissionRoute from '@/features/auth/components/RequirePermissionRoute'
import LoginPage from '@/features/auth/components/LoginPage'
import NoAccessPage from '@/features/auth/pages/NoAccessPage'
import LoadingSpinner from '@/shared/components/ui/LoadingSpinner'
import type { PermissionCode } from '@/features/auth/types'

const HomePage  = lazy(() => import('@/features/home/pages/HomePage'))
const MePage    = lazy(() => import('@/features/me/pages/MePage'))
const UsersPage = lazy(() => import('@/features/admin-users/pages/UsersPage'))
const LogsPage  = lazy(() => import('@/features/logs/pages/LogsPage'))
const UploadsPage      = lazy(() => import('@/features/bos/pages/UploadsPage'))
const UploadDetailPage = lazy(() => import('@/features/bos/pages/UploadDetailPage'))
const MappingPage       = lazy(() => import('@/features/bos/pages/MappingPage'))
const EntityHistoryPage = lazy(() => import('@/features/bos/pages/EntityHistoryPage'))
const ExceptionsPage    = lazy(() => import('@/features/bos/pages/ExceptionsPage'))
const LayoutPage        = lazy(() => import('@/features/bos/pages/LayoutPage'))
const FeeLinesPage      = lazy(() => import('@/features/fee-lines/pages/FeeLinesPage'))
const FeeLineNewPage    = lazy(() => import('@/features/fee-lines/pages/FeeLineNewPage'))
const FeeLineDetailPage = lazy(() => import('@/features/fee-lines/pages/FeeLineDetailPage'))
const ServicesPage      = lazy(() => import('@/features/services/pages/ServicesPage'))
const ServiceDetailPage = lazy(() => import('@/features/services/pages/ServiceDetailPage'))
const QboItemsPage      = lazy(() => import('@/features/services/pages/QboItemsPage'))
const BucketsPage       = lazy(() => import('@/features/services/pages/BucketsPage'))
const ActualsPage       = lazy(() => import('@/features/qbo-actuals/pages/ActualsPage'))
const ReferenceDataPage = lazy(() => import('@/features/reference-data/pages/ReferenceDataPage'))
const UnbilledPage      = lazy(() => import('@/features/unbilled/pages/UnbilledPage'))
const SuggestionsPage   = lazy(() => import('@/features/unbilled/pages/SuggestionsPage'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <LoadingSpinner />
    </div>
  )
}

/**
 * Wraps a lazy page in Suspense and, when a permission is given, a fail-closed
 * guard. The only place route access is declared (AUTH §4).
 */
function wrap(Page: React.ComponentType, permission?: PermissionCode) {
  const element = (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  )

  if (!permission) return element

  return <RequirePermissionRoute permission={permission}>{element}</RequirePermissionRoute>
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/no-access',
    element: (
      <ProtectedRoute>
        <NoAccessPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AuthGate>
          <Layout />
        </AuthGate>
      </ProtectedRoute>
    ),
    children: [
      { index: true,         element: wrap(HomePage) },
      { path: 'me',          element: wrap(MePage) },
      { path: 'admin/users', element: wrap(UsersPage, 'USER_ADMIN') },
      { path: 'admin/logs',  element: wrap(LogsPage, 'AUDIT_VIEW') },
      { path: 'bos/upload',        element: <Navigate to="/bos/uploads?new=upload" replace /> },
      { path: 'bos/uploads',       element: wrap(UploadsPage, 'BOS_VIEW') },
      { path: 'bos/uploads/:uploadId', element: wrap(UploadDetailPage, 'BOS_VIEW') },
      { path: 'bos/mapping',           element: wrap(MappingPage, 'BOS_MAPPING_VIEW') },
      { path: 'bos/mapping/exceptions', element: wrap(ExceptionsPage, 'BOS_VIEW') },
      { path: 'bos/mapping/layout',    element: wrap(LayoutPage, 'LAYOUT_EDIT') },
      { path: 'bos/entities/:tsCode',  element: wrap(EntityHistoryPage, 'BOS_VIEW') },
      { path: 'bos/exceptions',        element: <Navigate to="/bos/mapping/exceptions" replace /> },
      { path: 'bos/backfill',          element: <Navigate to="/bos/uploads?new=backfill" replace /> },
      { path: 'bos/layout',            element: <Navigate to="/bos/mapping/layout" replace /> },
      { path: 'billing/fee-lines',     element: wrap(FeeLinesPage, 'BILLING_VIEW') },
      { path: 'billing/fee-lines/new', element: wrap(FeeLineNewPage, 'FEE_LINE_EDIT') },
      { path: 'billing/fee-lines/:id', element: wrap(FeeLineDetailPage, 'BILLING_VIEW') },
      { path: 'billing/unbilled',      element: wrap(UnbilledPage, 'BILLING_VIEW') },
      { path: 'billing/suggestions',   element: wrap(SuggestionsPage, 'BILLING_VIEW') },
      { path: 'billing/services',      element: wrap(ServicesPage, 'BILLING_VIEW') },
      { path: 'billing/services/items', element: wrap(QboItemsPage, 'BILLING_VIEW') },
      { path: 'billing/services/buckets', element: wrap(BucketsPage, 'BILLING_VIEW') },
      { path: 'billing/actuals',       element: wrap(ActualsPage, 'BILLING_VIEW') },
      { path: 'reference/data',        element: wrap(ReferenceDataPage, 'BILLING_VIEW') },
      { path: 'billing/services/:id',  element: wrap(ServiceDetailPage, 'BILLING_VIEW') },
      { path: '*',           element: <Navigate to="/" replace /> },
    ],
  },
])
